/**
 * Granularity-aware date formatters for the report graphs' x-axis ticks and
 * hover tooltips.
 *
 * The report buckets are chosen by cadence (see {@link periodOptions}): a
 * *daily* report buckets **hourly**, so its 24 points all fall on the same
 * calendar day. Formatting those with a day-only label ("15 Sep 2026") printed
 * the same string on every tick and hid the hour on hover. For sub-daily
 * buckets we therefore include the time; for daily-and-coarser buckets we keep
 * the day-only label (each bucket is already a distinct day).
 *
 * All formatting is done in the report's timezone ({@link IANATimeZone}) so the
 * hour shown matches the timezone the data was bucketed in.
 */
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import type { Frequency } from "./frequency"

/** Frequencies whose buckets are shorter than a day (multiple points per day). */
const SUB_DAILY_FREQUENCIES: ReadonlySet<Frequency> = new Set<Frequency>(["5min", "hourly"])

/** True when a report's buckets are sub-daily (so ticks/hover need the time). */
export function isSubDaily(frequency: Frequency): boolean {
  return SUB_DAILY_FREQUENCIES.has(frequency)
}

/**
 * Build a date formatter for report graph ticks/tooltips at the given cadence.
 *
 * - Sub-daily (5min / hourly): "15 Sep, 14:00" — a distinct label per bucket
 *   that surfaces the hour on both the axis and the hover.
 * - Daily and coarser: "15 Sep 2026" — one label per day, unchanged.
 *
 * The returned function accepts the bucket's ISO time string (the value recharts
 * hands the `tickFormatter` / tooltip `label`) and tolerates a bad value by
 * echoing it back.
 */
export function reportDateFormatter(frequency: Frequency): (iso: string) => string {
  const timeZone = IANATimeZone()
  const options: Intl.DateTimeFormatOptions = isSubDaily(frequency)
    ? { timeZone, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }
    : { timeZone, day: "numeric", month: "short", year: "numeric" }
  const formatter = new Intl.DateTimeFormat([], options)

  return (iso: string): string => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return formatter.format(date)
  }
}
